import React, { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Copy, Check, MessageCircle } from "lucide-react";

/**
 * Botões de agilização do atendimento manual por WhatsApp:
 *  - Copiar link de acompanhamento do pedido (usa /pedido/{txid})
 *  - Abrir conversa no WhatsApp com mensagem pré-preenchida
 *
 * Props:
 *  - txid: string (obrigatório)
 *  - customerName: string
 *  - customerPhone: string (opcional; BR "11999998888" ou já com 55)
 *  - kind: "order" | "subscription" (afeta cópia)
 *  - testidPrefix: string (default "row")
 *  - compact: boolean (só ícone, sem texto)
 */
export default function CopyTrackButton({
  txid,
  customerName = "",
  customerPhone = "",
  kind = "order",
  testidPrefix = "row",
  compact = false,
}) {
  const [copied, setCopied] = useState(false);

  if (!txid) return null;

  const trackUrl = `${window.location.origin}/pedido/${txid}`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(trackUrl);
      setCopied(true);
      toast.success("Link copiado para a área de transferência");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for older browsers / non-secure contexts
      const ta = document.createElement("textarea");
      ta.value = trackUrl;
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand("copy");
        setCopied(true);
        toast.success("Link copiado");
        setTimeout(() => setCopied(false), 2000);
      } catch {
        toast.error("Não foi possível copiar — copie manualmente: " + trackUrl);
      }
      document.body.removeChild(ta);
    }
  };

  const openWhatsApp = () => {
    const firstName = (customerName || "").trim().split(/\s+/)[0] || "";
    const salutation = firstName ? `Oi ${firstName}! ` : "Oi! ";
    const kindWord = kind === "subscription" ? "sua inscrição" : "seu pedido";
    const message =
      `${salutation}Aqui é da equipe do 3º Encontrão Nacional Renascer. ` +
      `Segue o link para acompanhar ${kindWord}:\n\n${trackUrl}\n\n` +
      `Nele você vê o status, o QR Code PIX (se ainda não pagou) e todos os detalhes. ` +
      `Qualquer dúvida, é só chamar aqui! 💛`;

    // Normalize BR phone: strip non-digits and prefix "55" if missing
    let phone = (customerPhone || "").replace(/\D/g, "");
    if (phone && !phone.startsWith("55")) {
      phone = "55" + phone;
    }

    // If we have a phone → open direct chat, else use share URL
    const url = phone
      ? `https://wa.me/${phone}?text=${encodeURIComponent(message)}`
      : `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  if (compact) {
    return (
      <div className="inline-flex gap-1">
        <Button
          variant="outline"
          size="sm"
          onClick={copyLink}
          title="Copiar link de acompanhamento"
          className="h-8 w-8 p-0 rounded-full text-stone-700 border-stone-300 hover:bg-stone-100"
          data-testid={`${testidPrefix}-copy-link`}
        >
          {copied ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={openWhatsApp}
          title="Enviar link por WhatsApp"
          className="h-8 w-8 p-0 rounded-full text-emerald-800 border-emerald-300 hover:bg-emerald-50"
          data-testid={`${testidPrefix}-send-whatsapp`}
        >
          <MessageCircle className="h-3.5 w-3.5" />
        </Button>
      </div>
    );
  }

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={copyLink}
        className="text-stone-700 border-stone-300 hover:bg-stone-100"
        data-testid={`${testidPrefix}-copy-link`}
      >
        {copied ? (
          <><Check className="h-3.5 w-3.5 mr-1.5 text-emerald-600" /> Copiado</>
        ) : (
          <><Copy className="h-3.5 w-3.5 mr-1.5" /> Copiar link</>
        )}
      </Button>
      <Button
        variant="outline"
        size="sm"
        onClick={openWhatsApp}
        className="text-emerald-800 border-emerald-300 hover:bg-emerald-50"
        data-testid={`${testidPrefix}-send-whatsapp`}
      >
        <MessageCircle className="h-3.5 w-3.5 mr-1.5" /> WhatsApp
      </Button>
    </>
  );
}
